import { useEffect, useRef, useState } from 'react';
import { timeAxisTicks, formatAxisTime, nearestIndexByT } from './chartMath';

export interface ChartPoint { t: number; v: number; }

// Which vertical scale a series or area is measured against. Charts combining
// units (solar W/m² bars under a °C temperature line) put one on each.
export type ChartAxis = 'left' | 'right';

// A bar series — a per-day quantity.
export interface ChartBars {
  data: ChartPoint[];
  label: string;
  // Which y-scale to measure this series against. Defaults to 'left'.
  axis?: ChartAxis;
  // When false, bars are measured from the axis' domain minimum rather than
  // zero. A quantity with no meaningful zero (e.g. daily temperature) would
  // otherwise draw a bar from 0°C to its value and swamp the chart — this
  // renders it as a "column" spanning the visible domain instead. Defaults
  // to true, preserving the usual magnitude-from-zero bars (e.g. solar).
  fromZero?: boolean;
  // Per-point intensity (0..1) — e.g. cloud cover — mapped through a
  // one-hue sequential ramp (--seq-100 → --seq-700) instead of the flat
  // series colour. Matched to `data` by nearest timestamp; a bar with no
  // corresponding point falls back to the flat series colour.
  colorBy?: ChartPoint[];
  // Tooltip label for `colorBy` (e.g. "cloud cover"). `colorBy` has no axis
  // unit of its own, so it needs its own label rather than borrowing
  // `label`, which already names the bar's own quantity. Omit to leave
  // `colorBy` out of the tooltip.
  colorByLabel?: string;
  // A min/max whisker drawn through each bar (e.g. the day's temperature
  // spread) — the bar-chart equivalent of `ChartArea`'s envelope, which
  // only makes sense against a line.
  whisker?: { lo: ChartPoint[]; hi: ChartPoint[] };
}

export interface ChartSeries {
  data: ChartPoint[];
  label: string;
  dashed?: boolean;
  // Which y-scale to measure this series against. Defaults to 'left'.
  axis?: ChartAxis;
  // When true, this series is a noisy measured signal eligible for outlier
  // removal by FilterableHistoryChart. Reference lines (e.g. setpoint) leave
  // it unset so they are never filtered.
  filterable?: boolean;
  // When true, FilterableHistoryChart draws a flat line at this series'
  // mean over the visible range. Computed post-filter so the labelled value
  // always matches the line actually drawn.
  average?: boolean;
}

// A horizontal rule at a constant value, drawn in the owning series' colour.
export interface ChartRefLine {
  v: number;
  label: string;
  colorIndex: number;   // index into `series`, for colour matching
  // Which y-scale to measure this rule against. Defaults to 'left'. A rule
  // derived from a right-axis series (its mean, say) MUST say so, or it lands
  // at a plausible-looking but wrong height on the left domain.
  axis?: ChartAxis;
}

// A filled band between a low and a high series — e.g. the min/max spread of a
// daily rollup around its average. Drawn behind the lines.
export interface ChartArea {
  lo: ChartPoint[];
  hi: ChartPoint[];
  colorIndex: number;   // index into `series`, for colour matching
  // Which y-scale to measure this envelope against. Defaults to 'left'.
  axis?: ChartAxis;
}

export interface ChartBand {
  from: number;
  to: number;
  kind: 'heat' | 'cool';
  // Fraction of the band's span that actually called (0..1). Only meaningful
  // for aggregated data, where one band covers a whole bucket and the bucket
  // is marked calling if ANY sample in it called. Leave unset for raw data,
  // where a band IS a contiguous run of calls and is drawn at full strength.
  intensity?: number;
}

interface Props {
  // Maximum drawing width in px. The chart measures its container and shrinks
  // to fit on narrow viewports (phones), capping at this value on desktop so
  // it never overflows the card.
  width: number;
  height: number;
  series: ChartSeries[];
  bands?: ChartBand[];
  refLines?: ChartRefLine[];
  areas?: ChartArea[];
  bars?: ChartBars[];
  yLabel?: string;
  yLabelRight?: string;
  padding?: { l: number; r: number; t: number; b: number };
}

const SERIES_COLOR = (i: number) => (i === 0 ? 'var(--accent)' : 'var(--muted)');

// A one-hue sequential ramp for `ChartBars.colorBy` (e.g. cloud cover),
// mixed in OKLab so intermediate steps stay perceptually even. `t` is
// clamped so an out-of-range input (a stray >1 fraction) never inverts the
// ramp.
const seqColor = (t: number) => {
  const pct = Math.round(Math.max(0, Math.min(1, t)) * 100);
  return `color-mix(in oklab, var(--seq-700) ${pct}%, var(--seq-100))`;
};

export function HistoryChart({ width: maxWidth, height, series, bands = [], refLines = [], areas = [], bars = [], yLabel, yLabelRight, padding = { l: 40, r: 10, t: 10, b: 28 } }: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [hoverT, setHoverT] = useState<number | null>(null);
  // Measure the container so the SVG fills the available width on phones while
  // capping at maxWidth on desktop. Falls back to maxWidth when ResizeObserver
  // is unavailable (e.g. jsdom in tests) or before the first measurement.
  const [measuredW, setMeasuredW] = useState(0);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      if (w > 0) setMeasuredW(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const width = measuredW > 0 ? Math.min(maxWidth, measuredW) : maxWidth;

  // Plot PLOTTABLE points only. A caller mapping API rows straight to points
  // can hand us `v: undefined`/NaN — the bucketer omits a numeric field when
  // no sample in the bucket was finite — and a single non-finite value makes
  // vMin/vMax NaN, so every tick and path renders as NaN: a silently blank
  // chart with no explanation. If nothing is plottable, say so.
  // Both series and areas get the same treatment, so the predicate and the
  // comparator are defined once. Ascending sort matters most for areas —
  // rollups arrive newest-first, and a polygon built from `lo` forward and
  // `hi` reversed would self-intersect — but lines are sorted too rather than
  // left correct-by-luck. `.sort()` mutates, so it is only ever applied to the
  // fresh array `.filter()` returns, never to a caller's array.
  const isPlottable = (p: ChartPoint) => Number.isFinite(p.t) && Number.isFinite(p.v);
  const byT = (a: ChartPoint, b: ChartPoint) => a.t - b.t;
  const plot = series.map((s) => ({ ...s, data: s.data.filter(isPlottable).sort(byT) }));
  const finite = (pts: ChartPoint[]) => pts.filter(isPlottable).sort(byT);
  const plotAreas = areas.map((a) => ({ ...a, lo: finite(a.lo), hi: finite(a.hi) }));
  const plotBars = bars.map((b) => {
    const data = b.data.filter(isPlottable).sort(byT);
    // Spread `whisker` in only when present — under exactOptionalPropertyTypes,
    // `whisker: undefined` is a different (disallowed) shape from omitting the
    // key entirely.
    return b.whisker
      ? { ...b, data, whisker: { lo: finite(b.whisker.lo), hi: finite(b.whisker.hi) } }
      : { ...b, data };
  });
  // Split the plotted values by the scale they are measured against. The
  // envelope (and a bar's whisker, its bar-chart equivalent) by definition
  // reaches beyond the mark it wraps, so it has to join that axis' domain or
  // it would render clipped at the plot edges.
  const onRight = (a: ChartAxis | undefined) => a === 'right';
  const leftPoints = [
    ...plot.filter((s) => !onRight(s.axis)).flatMap((s) => s.data),
    ...plotAreas.filter((a) => !onRight(a.axis)).flatMap((a) => [...a.lo, ...a.hi]),
    ...plotBars.filter((b) => !onRight(b.axis)).flatMap((b) => b.data),
    ...plotBars.filter((b) => !onRight(b.axis) && b.whisker).flatMap((b) => [...b.whisker!.lo, ...b.whisker!.hi]),
  ];
  const rightPoints = [
    ...plot.filter((s) => onRight(s.axis)).flatMap((s) => s.data),
    ...plotAreas.filter((a) => onRight(a.axis)).flatMap((a) => [...a.lo, ...a.hi]),
    ...plotBars.filter((b) => onRight(b.axis)).flatMap((b) => b.data),
    ...plotBars.filter((b) => onRight(b.axis) && b.whisker).flatMap((b) => [...b.whisker!.lo, ...b.whisker!.hi]),
  ];
  const hasRight = rightPoints.length > 0;
  const hasLeft = leftPoints.length > 0;

  const domainPoints = [...leftPoints, ...rightPoints];
  if (domainPoints.length === 0) {
    return <div className="muted" style={{ padding: 'var(--sp-4)' }}>No data in this range yet.</div>;
  }
  const tMin = Math.min(...domainPoints.map((p) => p.t));
  const tMax = Math.max(...domainPoints.map((p) => p.t));

  // A zero-baseline bar (fromZero !== false, the default) is read by height,
  // so it needs a true zero baseline — starting the scale at the data
  // minimum would exaggerate the differences between bars. A non-zero bar
  // (fromZero: false — a "column" like daily temperature) has no such need,
  // so it doesn't force zero into either domain.
  // `leftPoints` is empty when every series and area sits on the right and
  // there are no left bars; fall back to the right values so the left scale
  // never yields NaN (no left axis is meaningful in that configuration
  // anyway). Count bar POINTS, not bar entries: a caller that passes `bars`
  // unconditionally still hands us an entry when the range returned nothing,
  // and clamping to zero for a series that never draws a bar squashes the
  // scale with nothing on screen to explain it.
  const fromZero = (b: ChartBars) => b.fromZero !== false;
  const hasLeftZeroBars = plotBars.some((b) => !onRight(b.axis) && fromZero(b) && b.data.length > 0);
  const hasRightZeroBars = plotBars.some((b) => onRight(b.axis) && fromZero(b) && b.data.length > 0);
  const leftValues = (leftPoints.length > 0 ? leftPoints : rightPoints).map((p) => p.v);
  const vMinLeft = hasLeftZeroBars ? Math.min(0, ...leftValues) : Math.min(...leftValues) - 0.5;
  const vMaxLeft = Math.max(...leftValues) + 0.5;
  const vMinRight = !hasRight ? 0 : hasRightZeroBars ? Math.min(0, ...rightPoints.map((p) => p.v)) : Math.min(...rightPoints.map((p) => p.v)) - 0.5;
  const vMaxRight = hasRight ? Math.max(...rightPoints.map((p) => p.v)) + 0.5 : 1;

  // Reserve gutter for the right axis here rather than making callers widen
  // `padding` — a caller that forgot would silently clip its own labels. 34px
  // is sized for a two-digit `toFixed(1)` value plus a short unit ("20.5 °C");
  // a longer unit on the right axis would want more.
  const rightGutter = hasRight ? 34 : 0;
  const innerW = width - padding.l - padding.r - rightGutter;
  const innerH = height - padding.t - padding.b;
  const plotRight = padding.l + innerW;
  const x = (t: number) => padding.l + ((t - tMin) / (tMax - tMin || 1)) * innerW;
  const yOn = (v: number, axis: ChartAxis) =>
    axis === 'right'
      ? padding.t + (1 - (v - vMinRight) / (vMaxRight - vMinRight || 1)) * innerH
      : padding.t + (1 - (v - vMinLeft) / (vMaxLeft - vMinLeft || 1)) * innerH;
  // Left-scale shorthand, for the ticks, reference lines and hover crosshair.
  const y = (v: number) => yOn(v, 'left');

  // One point in path coordinates, shared by every path builder below.
  const pt = (p: ChartPoint, axis: ChartAxis = 'left') => `${x(p.t).toFixed(1)} ${yOn(p.v, axis).toFixed(1)}`;

  function pathOf(points: ChartPoint[], axis: ChartAxis = 'left'): string {
    return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${pt(p, axis)}`).join(' ');
  }

  // Sweep left→right along the high edge, then right→left back along the low
  // edge, and close. Both inputs are already ascending (see `finite`).
  function areaPathOf(a: { lo: ChartPoint[]; hi: ChartPoint[] }, axis: ChartAxis = 'left'): string {
    if (a.lo.length === 0 || a.hi.length === 0) return '';
    const up = a.hi.map((p, i) => `${i === 0 ? 'M' : 'L'} ${pt(p, axis)}`).join(' ');
    const down = [...a.lo].reverse().map((p) => `L ${pt(p, axis)}`).join(' ');
    return `${up} ${down} Z`;
  }

  // 5 evenly spaced y ticks per axis (top tick carries the unit label).
  const yTicks = Array.from({ length: 5 }, (_, i) => vMinLeft + (i / 4) * (vMaxLeft - vMinLeft));
  const yTicksRight = Array.from({ length: 5 }, (_, i) => vMinRight + (i / 4) * (vMaxRight - vMinRight));
  const xTicks = timeAxisTicks(tMin, tMax, 4);
  const spanMs = tMax - tMin;

  // --- Hover state ------------------------------------------------------
  // Snap the crosshair to the nearest point of the first non-empty series.
  // `plot` can legitimately be empty now that the domain (and so the
  // empty-state guard) also counts area points — a caller may pass only an
  // envelope. nearestIndexByT returns -1 for empty data, so the crosshair
  // simply never snaps, rather than the non-null assertion handing us
  // `undefined` and throwing on the first mousemove.
  // Deliberate: `primary` comes from `plot` alone, so a BARS-ONLY chart
  // (`series={[]}`) has no hover and no tooltip. That is not an oversight —
  // every chart shipping bars pairs them with a line (the weather card's
  // temperature), and that line drives the crosshair with the bars reading out
  // alongside it. Revisit only if a bars-only chart ever ships.
  const primary = plot.find((s) => s.data.length) ?? plot[0] ?? { data: [], label: '' };
  const hoverPrimaryIdx = hoverT === null ? -1 : nearestIndexByT(primary.data, hoverT);
  const snappedT = hoverPrimaryIdx >= 0 ? primary.data[hoverPrimaryIdx]!.t : null;

  function handleMove(e: React.MouseEvent) {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = e.clientX - rect.left;
    // Clamp to `plotRight`, not `width - padding.r`: with a right-axis gutter
    // those differ by 34px, and `x()` maps tMax to plotRight, so the inverse
    // below would read a pointer in the gutter as a time past tMax. Snapping
    // hides that today, but the forward and inverse transforms should agree.
    const clamped = Math.max(padding.l, Math.min(plotRight, px));
    setHoverT(tMin + ((clamped - padding.l) / (innerW || 1)) * (tMax - tMin));
  }

  // Per-series readout at the snapped time, for the tooltip + dots. The axis
  // travels with the readout so the marker lands on the line it belongs to and
  // the tooltip reports that axis' unit.
  type Readout = { label: string; p: ChartPoint; axis: ChartAxis };
  const readouts: Readout[] = snappedT === null ? [] : plot.map((s) => {
    const idx = nearestIndexByT(s.data, snappedT);
    return idx >= 0 ? { label: s.label, p: s.data[idx]!, axis: s.axis ?? 'left' } : null;
  }).filter((r): r is Readout => r !== null);

  // Bars read out alongside the lines, each in its own axis' unit.
  const barReadouts: Readout[] = snappedT === null ? [] : plotBars.map((b): Readout | null => {
    const idx = nearestIndexByT(b.data, snappedT);
    return idx >= 0 ? { label: b.label, p: b.data[idx]!, axis: b.axis ?? 'left' } : null;
  }).filter((r): r is Readout => r !== null);

  // `colorBy` has no axis of its own, so it reads out as a plain percentage
  // rather than borrowing yLabel/yLabelRight.
  const colorByReadouts: { label: string; v: number }[] = snappedT === null ? [] : plotBars.flatMap((b) => {
    if (!b.colorBy || b.colorBy.length === 0 || !b.colorByLabel) return [];
    const idx = nearestIndexByT(b.colorBy, snappedT);
    return idx >= 0 ? [{ label: b.colorByLabel, v: b.colorBy[idx]!.v }] : [];
  });

  const hx = snappedT === null ? 0 : x(snappedT);
  // Flip the tooltip to the left of the crosshair when near the right edge.
  const tooltipRight = hx > padding.l + innerW / 2;

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%', maxWidth }}>
      <svg
        ref={svgRef}
        width={width}
        height={height}
        role="img"
        aria-label="History chart"
        onMouseMove={handleMove}
        onMouseLeave={() => setHoverT(null)}
      >
        {/* Bars: a per-day quantity, behind the lines and envelopes. */}
        {plotBars.map((b, bi) => {
          const axis = b.axis ?? 'left';
          const barIsFromZero = fromZero(b);
          const floorValue = barIsFromZero ? 0 : (axis === 'right' ? vMinRight : vMinLeft);
          // Clamped to the plot in case the floor value is off-domain.
          const floorY = Math.min(padding.t + innerH, Math.max(padding.t, yOn(floorValue, axis)));
          return b.data.map((p, i) => {
            // Sizing every bar from `innerW / count` assumes points are
            // spaced evenly across the whole range. Real data isn't — a
            // failed poll leaves a gap (see poll-weather.ts), so two points
            // can sit much closer together in time than the dataset's
            // average spacing. Capping each bar's width at its own local gap
            // (in pixels, via the same time→x scale as everything else)
            // keeps it from overlapping its neighbour there, while the
            // evenly-spaced case (the common one) is unaffected — local gap
            // and average gap coincide, so the cap never bites.
            const uniformW = innerW / Math.max(1, b.data.length);
            const prevGap = i > 0 ? p.t - b.data[i - 1]!.t : Infinity;
            const nextGap = i < b.data.length - 1 ? b.data[i + 1]!.t - p.t : Infinity;
            const localGapMs = Math.min(prevGap, nextGap);
            const localGapPx = Number.isFinite(localGapMs) ? (localGapMs / (tMax - tMin || 1)) * innerW : uniformW;
            const barW = Math.max(1, Math.min(uniformW, localGapPx) * 0.7);
            const top = yOn(p.v, axis);
            // Bars are centred on their point, but width divides by n while
            // spacing divides by n-1 — so the end bars hang half a bar past
            // each edge. At 7 points (the Trends 7d range) that is ~30px, right
            // over the y-tick label column. Clamp them back into the plot.
            const bx = Math.max(padding.l, Math.min(plotRight - barW, x(p.t) - barW / 2));
            const byColor = b.colorBy && b.colorBy.length > 0 ? b.colorBy[nearestIndexByT(b.colorBy, p.t)] : undefined;
            const fill = byColor
              ? seqColor(byColor.v)
              // Offset past the series palette so bars never take the accent —
              // on the weather card the solar line and the temperature bars
              // would otherwise differ only in opacity. SERIES_COLOR has just
              // two values, so this guarantees only that: with at least one
              // series, bars avoid series[0]'s accent. Bars still share
              // `var(--muted)` with series[1..n], distinguished by opacity.
              : SERIES_COLOR(series.length + bi);
            return (
              <rect
                key={`bar${bi}-${i}`}
                data-chart-bar
                x={bx}
                y={Math.min(top, floorY)}
                width={barW}
                height={Math.abs(floorY - top)}
                fill={fill}
                // A colour-ramped bar carries its own information in that
                // colour — translucency would just wash it back toward the
                // surface. Flat-colour bars keep the original soft opacity.
                opacity={byColor ? 1 : 0.35}
              />
            );
          });
        })}
        {/* Whiskers: a bar chart's equivalent of an envelope — drawn through
            each bar rather than as a filled band, since a band only reads
            against a line. */}
        {plotBars.map((b, bi) => {
          if (!b.whisker) return null;
          const axis = b.axis ?? 'left';
          const color = b.colorBy ? undefined : SERIES_COLOR(series.length + bi);
          return b.data.map((p, i) => {
            const loIdx = nearestIndexByT(b.whisker!.lo, p.t);
            const hiIdx = nearestIndexByT(b.whisker!.hi, p.t);
            if (loIdx < 0 || hiIdx < 0) return null;
            const cx = Math.max(padding.l, Math.min(plotRight, x(p.t)));
            const yLo = yOn(b.whisker!.lo[loIdx]!.v, axis);
            const yHi = yOn(b.whisker!.hi[hiIdx]!.v, axis);
            const stroke = color ?? seqColor(b.colorBy![nearestIndexByT(b.colorBy!, p.t)]?.v ?? 0);
            return (
              <g key={`whisker${bi}-${i}`} data-chart-whisker>
                <line x1={cx} x2={cx} y1={yHi} y2={yLo} stroke={stroke} strokeWidth={1.5} />
                <line x1={cx - 3} x2={cx + 3} y1={yHi} y2={yHi} stroke={stroke} strokeWidth={1.5} />
                <line x1={cx - 3} x2={cx + 3} y1={yLo} y2={yLo} stroke={stroke} strokeWidth={1.5} />
              </g>
            );
          });
        })}
        {/* Y axis (left). The gridlines always draw — they are the plot's
            horizontal rules whichever scale is in play — but the LABELS are
            suppressed when nothing is measured against the left scale, since
            it has then fallen back to the right values and would just repeat
            the right column's numbers down the other side. */}
        {yTicks.map((t, i) => (
          <g key={`y${i}`}>
            <line x1={padding.l} x2={plotRight} y1={y(t)} y2={y(t)} stroke="var(--border)" />
            {hasLeft && (
              <text x={padding.l - 6} y={y(t) + 4} fontSize="10" fill="var(--muted)" textAnchor="end">
                {t.toFixed(1)}{i === yTicks.length - 1 && yLabel ? ` ${yLabel}` : ''}
              </text>
            )}
          </g>
        ))}
        {/* Right axis, only when a series or area asked for it */}
        {hasRight && yTicksRight.map((t, i, arr) => (
          <text
            key={`yr${i}`}
            x={plotRight + 6}
            y={yOn(t, 'right') + 4}
            fontSize="10"
            fill="var(--muted)"
            textAnchor="start"
          >
            {t.toFixed(1)}{i === arr.length - 1 && yLabelRight ? ` ${yLabelRight}` : ''}
          </text>
        ))}
        {/* X axis time labels */}
        {xTicks.map((t, i) => (
          <text
            key={`x${i}`}
            x={x(t)}
            y={height - padding.b + 14}
            fontSize="10"
            fill="var(--muted)"
            textAnchor={i === 0 ? 'start' : i === xTicks.length - 1 ? 'end' : 'middle'}
          >
            {formatAxisTime(t, spanMs)}
          </text>
        ))}
        {/* Min/max envelopes, behind everything else so the lines stay legible */}
        {plotAreas.map((a, i) => (
          <path
            key={`a${i}`}
            data-chart-area
            d={areaPathOf(a, a.axis ?? 'left')}
            fill={SERIES_COLOR(a.colorIndex)}
            opacity={0.15}
            stroke="none"
          />
        ))}
        {/* Call bands. Without `intensity` (raw data) the band is a real run
            of calls, drawn at full strength. With it (aggregated data) the
            band spans a whole bucket that may have called only briefly, so
            fade it by the duty fraction — floored at 0.15 so a short call
            stays visible rather than becoming invisible. */}
        {bands.map((b, i) => {
          // Normalise the endpoints before measuring. The Math.max(1, …) below
          // is only there to keep a genuinely tiny band clickable-visible; left
          // to absorb an INVERTED band it turned a real ordering bug into a
          // silent 1px sliver. An ordering regression should now render
          // wrong-but-visible instead.
          const [a, z] = b.from <= b.to ? [b.from, b.to] : [b.to, b.from];
          return (
            <rect key={`b${i}`} data-band x={x(a)} y={padding.t} width={Math.max(1, x(z) - x(a))} height={innerH} fill={b.kind === 'heat' ? 'var(--accent-soft)' : 'var(--cool-soft)'} opacity={b.intensity === undefined ? 0.6 : 0.15 + 0.45 * b.intensity} />
          );
        })}
        {/* Series */}
        {plot.map((s, i) => (
          <path key={`s${i}`} d={pathOf(s.data, s.axis ?? 'left')} fill="none" stroke={SERIES_COLOR(i)} strokeWidth={1.5} strokeDasharray={s.dashed ? '4 3' : undefined} />
        ))}
        {/* Period-average (and any other constant) reference lines */}
        {refLines.map((r, i) => (
          <g key={`r${i}`}>
            <line
              data-ref-line
              x1={padding.l}
              x2={plotRight}
              y1={yOn(r.v, r.axis ?? 'left')}
              y2={yOn(r.v, r.axis ?? 'left')}
              stroke={SERIES_COLOR(r.colorIndex)}
              strokeWidth={1}
              opacity={0.5}
            />
            <text
              data-ref-label
              x={plotRight - 4}
              y={yOn(r.v, r.axis ?? 'left') - 4}
              fontSize="10"
              fill={SERIES_COLOR(r.colorIndex)}
              textAnchor="end"
            >
              {r.label}
            </text>
          </g>
        ))}
        {/* Hover crosshair + point markers */}
        {snappedT !== null && (
          <g data-chart-hover>
            <line x1={hx} x2={hx} y1={padding.t} y2={height - padding.b} stroke="var(--muted)" strokeDasharray="2 2" />
            {readouts.map((r, i) => (
              <circle key={`h${i}`} cx={x(r.p.t)} cy={yOn(r.p.v, r.axis)} r={3} fill={SERIES_COLOR(plot.findIndex((s) => s.label === r.label))} />
            ))}
          </g>
        )}
      </svg>
      {snappedT !== null && readouts.length > 0 && (
        <div
          data-chart-tooltip
          style={{
            position: 'absolute',
            left: hx,
            top: padding.t,
            transform: tooltipRight ? 'translate(-100%, 0)' : 'translate(0, 0)',
            marginLeft: tooltipRight ? -8 : 8,
            pointerEvents: 'none',
            background: 'var(--bg, #fff)',
            border: '1px solid var(--border)',
            borderRadius: 4,
            padding: '4px 6px',
            fontSize: 'var(--fs-sm)',
            whiteSpace: 'nowrap',
            boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
          }}
        >
          <div className="muted" style={{ marginBottom: 2 }}>{formatAxisTime(snappedT, spanMs)}</div>
          {[...readouts, ...barReadouts].map((r, i) => {
            // Each readout carries its own axis' unit — with two scales in
            // play, labelling a °C line "W/m²" would be plainly wrong.
            const unit = r.axis === 'right' ? yLabelRight : yLabel;
            return (
              <div key={`t${i}`} className="tabular">
                {r.label}: {r.p.v.toFixed(1)}{unit ? ` ${unit}` : ''}
              </div>
            );
          })}
          {colorByReadouts.map((r, i) => (
            <div key={`cb${i}`} className="tabular">
              {r.label}: {Math.round(r.v * 100)}%
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
